/**
 * breakeven.ts — общий расчёт «насколько цена должна сойти, чтобы окупить премию».
 *
 * Презентационный слой: чистая математика поверх данных бэка, формулы движка не дублируются.
 * Структура BreakevenMove задумана как переносимый параметр между интерфейсами
 * (Аналитик ↔ Вотчлист и пр.) — один тип, одни функции-расчёта.
 */

import type { PayoffResponse } from "../api";

// «Подушка» (консерватив) — депозит/облигация/кэш. НЕ влияет на профиль премий.
const CUSHION = new Set(["deposit", "bond", "cash"]);

/** Сумма P&L по «ценовым» ногам (опционы/фьючерсы/акции), БЕЗ подушки. */
export function priceLegSum(p: PayoffResponse, dateIdx: number): number[] {
  const ids = p.legs_meta.filter((m) => !CUSHION.has(m.type)).map((m) => m.id);
  return p.prices.map((_, j) => ids.reduce((s, id) => s + (p.pnl_per_leg[id]?.[dateIdx]?.[j] ?? 0), 0));
}

/** Есть ли в портфеле подушка (депозит/облигация/кэш). */
export function hasCushion(p: PayoffResponse): boolean {
  return p.legs_meta.some((m) => CUSHION.has(m.type));
}

/** Точки пересечения нуля (линейная интерполяция) — breakeven по премиям. */
export function zeroCrossings(xs: number[], ys: number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < xs.length; i++) {
    const a = ys[i - 1], b = ys[i];
    if ((a <= 0 && b > 0) || (a >= 0 && b < 0)) {
      const t = a === b ? 0 : a / (a - b);
      out.push(xs[i - 1] + t * (xs[i] - xs[i - 1]));
    }
  }
  return out;
}

/** Переносимый параметр: точки нуля и ход цены вниз/вверх (в %) до окупаемости премии. */
export interface BreakevenMove {
  lowBE: number | null;
  highBE: number | null;
  downPct: number | null; // на сколько % ВНИЗ от спота до нижней точки нуля
  upPct: number | null;   // на сколько % ВВЕРХ от спота до верхней точки нуля
}

/** Из набора точек нуля и спота — ближайшие BE снизу/сверху и % хода. */
export function breakevenMove(spot: number, bes: number[]): BreakevenMove {
  const below = bes.filter((b) => b <= spot);
  const above = bes.filter((b) => b >= spot);
  const lowBE = below.length ? Math.max(...below) : null;
  const highBE = above.length ? Math.min(...above) : null;
  return {
    lowBE, highBE,
    downPct: lowBE != null && spot > 0 ? ((spot - lowBE) / spot) * 100 : null,
    upPct: highBE != null && spot > 0 ? ((highBE - spot) / spot) * 100 : null,
  };
}

/** Из профиля payoff (экспирация, без подушки) — переносимый BreakevenMove. */
export function breakevenFromPayoff(p: PayoffResponse, spot: number): BreakevenMove {
  const ys = priceLegSum(p, p.dates.length - 1);
  return breakevenMove(spot, zeroCrossings(p.prices, ys));
}

/** Breakeven стрэнгла из страйков и премий за акцию (для Вотчлиста и др.). */
export function strangleBreakevenMove(spot: number, callStrike: number, putStrike: number, callPrem: number, putPrem: number): BreakevenMove {
  const net = callPrem + putPrem;
  return breakevenMove(spot, [putStrike - net, callStrike + net]);
}
